/** @jsx isolateComponent */

import { css } from '@emotion/core';
import { Fragment, ReactNode, useState } from 'react';

import { AsyncIndicator, FlexLayout } from '@zapier/common-components';
import { isolateComponent } from '@zapier/common-utils';
import { Button, Colors, Link } from '@zapier/design-system';
import { Price } from '@zapier/design-system-revenue';

import {
  sendFullStoryEvent,
  useSendFullStoryEvent,
  useTrackingContext,
} from '@zapier/toolbox-tracking';

import { tracking } from '@zapier/promotions/src/utils';

import { OfferModal } from '@zapier/promotions/src/components';
import { useSize } from '@zapier/promotions/src/hooks';

import { useData as _useData } from './useData';

const rootStyles = css`
  a {
    text-decoration: underline;
  }
`;

const buttonColorThemeBase = {
  backgroundColor: 'transparent',
  color: Colors.white,
  padding: '0 10px',
  textDecoration: 'underline',
};

const buttonColorTheme = {
  custom: {
    base: buttonColorThemeBase,
    disabled: buttonColorThemeBase,
    hover: buttonColorThemeBase,
    focus: buttonColorThemeBase,
  },
};

type BannerButtonProps = {
  children: ReactNode;
  onClick: () => void;
};

const BannerButton = ({ children, onClick }: BannerButtonProps) => (
  <Button
    color="custom"
    colorTheme={buttonColorTheme}
    isPadded={false}
    onClick={onClick}
    size="xsmall"
  >
    {children}
  </Button>
);

type Props = {
  onDismiss: () => void;
  /** For dependency injection testing only */
  useData?: () => ReturnType<typeof _useData>;
};

export const ReducedTaskTeamCompanyTopBarBanner = ({
  onDismiss,
  useData = _useData,
}: Props) => {
  const { discount, error, isLoading, pendingOfferId, planType } = useData();

  const [isShowingOfferModal, setIsShowingOfferModal] = useState(false);

  const eventProperties = {
    planType,
  };

  useSendFullStoryEvent(
    'Viewed Reduced Task Team/Company Promo Banner',
    eventProperties
  );

  const size = useSize();
  const { trackEvent } = useTrackingContext();

  if (isLoading) {
    return <AsyncIndicator />;
  }

  if (error) {
    return <Fragment>{error}</Fragment>;
  }

  const featureText =
    planType === 'Team'
      ? 'Invite your entire team.'
      : 'Have more control over your Zaps, users, and data.';

  return (
    <div css={rootStyles}>
      <FlexLayout
        align="center"
        gutter="none"
        justify={size === 'large' ? 'space-between' : 'center'}
      >
        <div>
          <strong>Last Chance - {featureText}</strong> Upgrade to a{' '}
          <Link color="white" href="/app/billing/plans">
            {planType} account
          </Link>{' '}
          now for <Price>{discount}</Price> less.
        </div>

        <div>
          <BannerButton
            onClick={() => {
              trackEvent(
                tracking.reducedTaskTeamCompanyPromo.TOP_BAR_BANNER_UPGRADE,
                eventProperties
              );

              sendFullStoryEvent(
                'Clicked Upgrade on Reduced Task Team/Company Promo Banner',
                eventProperties
              );
              setIsShowingOfferModal(true);
            }}
          >
            Upgrade Now
          </BannerButton>

          <BannerButton
            onClick={() => {
              trackEvent(
                tracking.reducedTaskTeamCompanyPromo.TOP_BAR_BANNER_DISMISS,
                eventProperties
              );

              sendFullStoryEvent(
                'Dismissed Reduced Task Team/Company Promo Banner',
                eventProperties
              );
              onDismiss();
            }}
          >
            No, Thanks
          </BannerButton>
        </div>
      </FlexLayout>

      {isShowingOfferModal && (
        <OfferModal
          offerId={pendingOfferId}
          onClose={() => setIsShowingOfferModal(false)}
        />
      )}
    </div>
  );
};
